import React, { useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, checkBetaStatus } from './firebase';
import { setUser, setBetaStatus, setAdminStatus, clearUser } from './redux/authSlice';
import LandingPage from './LandingPage';
import Registration from './Registration';
import Login from './Login';
import Dashboard from './Dashboard';
import BetaDashboard from './BetaDashboard';
import AdminPanel from './AdminPanel';
import ErrorBoundary from './components/ErrorBoundary';
import './App.css';

function App() {
  const dispatch = useDispatch();
  const { isAuthenticated, isAdmin, isBetaUser, isLoading } = useSelector((state) => state.auth);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        dispatch(setUser(user));

        try {
          // Call server function to set/verify beta and admin claims
          await checkBetaStatus();

          // Force token refresh to get updated claims
          await user.getIdToken(true);

          // Now read the updated claims
          const idTokenResult = await user.getIdTokenResult();
          const isBeta = idTokenResult.claims.beta === true;
          const isAdmin = idTokenResult.claims.admin === true;
          dispatch(setBetaStatus(isBeta));
          dispatch(setAdminStatus(isAdmin));
        } catch (error) {
          console.error('Error checking beta status:', error);
          dispatch(setBetaStatus(false));
          dispatch(setAdminStatus(false));
        }
      } else {
        dispatch(clearUser());
      }
    });

    return () => unsubscribe();
  }, [dispatch]);

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="App" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <Router>
      <div className="App">
        <ErrorBoundary>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/register" element={<Registration />} />
            <Route path="/login" element={<Login />} />
            <Route
              path="/dashboard"
              element={
                isAuthenticated
                  ? (isBetaUser ? <BetaDashboard /> : <Dashboard />)
                  : <Navigate to="/login" />
              }
            />
            <Route
              path="/admin"
              element={isAuthenticated && isAdmin ? <AdminPanel /> : <Navigate to="/dashboard" />}
            />
          </Routes>
        </ErrorBoundary>
      </div>
    </Router>
  );
}

export default App;